import {t} from 'i18next';
import {useCallback, useEffect, useState} from 'react';
import {showSnackbar, SnackbarType} from 'webapp-lib/Functions';

import {Folder, FolderType} from '#/FolderTree/Folder';
import backend, {
  FolderInResponse,
  GetFolderContentTypesResponse,
} from '@lexian/api/documents-api';

import {showDeleteFolderDialog} from './deleteFolder';
import {ArtifactEntityType, ArtifactEntityTypes} from './types';

export const isAllItemsFolder = (folderId: string): boolean =>
  folderId === FolderType.AllItems;
export const isTrashFolder = (folderId: string): boolean =>
  folderId === FolderType.Trash;

export const loadSubfolders = (
  casusId: string,
  folderId: string
): Promise<FolderInResponse[]> =>
  new Promise<FolderInResponse[]>((resolve) => {
    backend.getFolders({
      parameters: {
        casusId,
        folderId,
      },
      onSuccess: ({body}) => resolve(body.folders),
    });
  });

export const fetchFolderContentTypes = (
  casusId: string,
  folderId: string
): Promise<GetFolderContentTypesResponse> =>
  new Promise<GetFolderContentTypesResponse>((resolve) => {
    backend.getFolderContentTypes({
      parameters: {
        casusId,
        folderId,
      },
      onSuccess: ({body}) => resolve(body),
    });
  });

export const useDeletingFolder = (
  casusId: string,
  handleOnSuccess: (folder: Folder) => Promise<void>,
  artifactEntityType?: ArtifactEntityType
): React.Dispatch<React.SetStateAction<Folder | undefined>> => {
  const [deletingFolder, setDeletingFolder] = useState<Folder>();

  const handleDeleteDecision = useCallback(async (): Promise<void> => {
    if (!deletingFolder) {
      return;
    }
    await deleteFolder(casusId, deletingFolder, handleOnSuccess);
  }, [casusId, deletingFolder, handleOnSuccess]);

  useEffect(() => {
    if (!deletingFolder || !artifactEntityType) {
      return;
    }
    const executeDeleteFolderDialog = async () => {
      const folderContentTypes = await fetchFolderContentTypes(
        casusId,
        deletingFolder.id
      );
      const deletionExtraInfo = getDeletionExtraInfo(
        artifactEntityType,
        folderContentTypes
      );
      showDeleteFolderDialog(handleDeleteDecision, deletionExtraInfo);
    };
    executeDeleteFolderDialog();
    setDeletingFolder(undefined);
  }, [artifactEntityType, casusId, deletingFolder, handleDeleteDecision]);

  return setDeletingFolder;
};

const getDeletionExtraInfo = (
  artifactEntityType: ArtifactEntityType,
  folderContentTypes: GetFolderContentTypesResponse
): string | undefined => {
  if (
    artifactEntityType === ArtifactEntityTypes.DOCUMENT &&
    folderContentTypes.containsEmails
  ) {
    return t('common:folders.delete.dialog.containsEmailsInfo').toString();
  }
  if (
    artifactEntityType === ArtifactEntityTypes.EMAIL &&
    folderContentTypes.containsDocuments
  ) {
    return t('common:folders.delete.dialog.containsDocumentsInfo').toString();
  }
  return undefined;
};

export const deleteFolder = async (
  casusId: string,
  folder: Folder,
  handleOnSuccess: (folder: Folder) => Promise<void>
): Promise<void> => {
  await backend.deleteFolder({
    parameters: {
      casusId,
      folderId: folder.id,
    },
    onSuccess: async () => {
      await handleOnSuccess(folder);
      showSnackbar({
        text: t('common:folders.delete.success'),
        type: SnackbarType.DELETE,
      });
    },
    localHandlers: {
      DocumentInUseCondition: () => {
        showSnackbar({
          text: t('common:folders.delete.dialog.documentInUseCondition'),
          type: SnackbarType.WARNING,
        });
      },
      DocumentLockedCondition: () => {
        showSnackbar({
          text: t('common:folders.delete.dialog.documentLockedCondition'),
          type: SnackbarType.WARNING,
        });
      },
    },
  });
};
